import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'flowbite-react';
import { Project } from '../../models';
import { Icon, Button, Alert } from '..';
import ReactTimeAgo from 'react-time-ago';
import moment from 'moment';
import { Projects } from '../../services';
import { loadProject, useAppDispatch, useAppSelector } from '../../redux';

interface ProjectChecksProps {
    project: Project;
}

function ProjectChecks(props: ProjectChecksProps) {
    const { project } = props;

    const [checksType, setChecksType] = useState('dependencies');
    const [checking, setChecking] = useState(false);
    const [checkError, setCheckError] = useState<string | null>(null);

    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector(state => state.auth);

    const { dependencies } = (project?.checks || {});

    const today = moment();

    const outdatedSections = React.useMemo(() => {
        if (!dependencies?.packages?.length)
            return { sections: null, count: 0 };

        const majors = dependencies.packages.filter((p: any) => p.status === 'MAJOR');
        const minors = dependencies.packages.filter((p: any) => p.status === 'MINOR');
        const patchs = dependencies.packages.filter((p: any) => p.status === 'PATCH');
        const count = majors.length + minors.length + patchs.length;

        const sections: any = [];

        if (majors?.length > 0) {
            sections.push(<h2 key="majors-title" className="font-bold text-xl mt-2 mb-1">{t('projects.majors')} ({majors.length})</h2>);
            majors?.forEach((p: any, index: number) => {
                sections.push((
                    <div key={`major-${p.package}-${index}`} className="flex flex-col sm:flex-row justify-between border rounded mb-1">
                        <div className="grow font-normal p-2">{ p.package }</div>
                        <div className="flex-none w-full sm:w-64 font-light p-2 bg-red-900 text-white rounded-b sm:rounded-l-none sm:rounded-r text-center">
                            { p.installed } {"=>"} { p.latest }
                        </div>
                    </div>
                ));
            });
        }

        if (minors?.length > 0) {
            sections.push(<h2 key="minors-title" className="font-bold text-xl mt-2 mb-1">{t('projects.minors')} ({minors.length})</h2>);
            minors?.forEach((p: any, index: number) => {
                sections.push((
                    <div key={`minor-${p.package}-${index}`} className="flex flex-col sm:flex-row justify-between border rounded mb-1">
                        <div className="grow font-normal p-2">{ p.package }</div>
                        <div className="flex-none w-full sm:w-64 font-light p-2 bg-orange-700 text-white rounded-b sm:rounded-l-none sm:rounded-r text-center">
                            { p.installed } {"=>"} { p.latest }
                        </div>
                    </div>
                ));
            });
        }

        if (patchs?.length > 0) {
            sections.push(<h2 key="patchs-title" className="font-bold text-xl mt-2 mb-1">{t('projects.patchs')} ({patchs.length})</h2>);
            patchs?.forEach((p: any, index: number) => {
                sections.push((
                    <div key={`patch-${p.package}-${index}`} className="flex flex-col sm:flex-row justify-between border rounded mb-1">
                        <div className="grow font-normal p-2">{ p.package }</div>
                        <div className="flex-none w-full sm:w-64 font-light p-2 bg-yellow-700 text-white rounded-b sm:rounded-l-none sm:rounded-r text-center">
                            { p.installed } {"=>"} { p.latest }
                        </div>
                    </div>
                ));
            });
        }

        return { sections: count > 0 ? sections : null, count };
    }, [ dependencies, t ]);

    const hasCheckResult = !!project?.hasDependeciesCheckResult?.();
    const isUpToDate = hasCheckResult && outdatedSections.count === 0;

    const handleRefresh = async () => {
        if (!project?._id || checking)
            return;

        setChecking(true);
        setCheckError(null);
        try {
            await Projects.checkDependencies(project._id);
            await dispatch(loadProject(project._id));
        } catch (err: any) {
            setCheckError(err?.message || t('projects.checks_refresh_failed'));
        } finally {
            setChecking(false);
        }
    };

    return (
        <Card className="ProjectChecks">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h2 className="text-2xl font-bold leading-7 flex items-center text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
                    <Icon type="check" size={7} className="mr-1.5" />
                    {t('projects.checks')}
                </h2>
                {user?.can('project:sync') && project?.hasConfiguredRepo?.() && (
                    <Button
                        title={t('projects.checks_refresh')}
                        icon="sync"
                        color="secondary"
                        small
                        loading={checking}
                        disabled={checking}
                        onClick={handleRefresh}
                    />
                )}
            </div>

            {checkError && (
                <Alert title={checkError} color="failure" icon="warning" />
            )}

            <div className="grid grid-cols-12 gap-4">
                <div className="col-span-full md:col-span-3">
                    <div
                        className={`cursor-pointer w-full border ${checksType === 'dependencies' ? "bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200" : "bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"} p-2 my-1 rounded-md`}
                        onClick={() => setChecksType('dependencies')}
                    >
                        { t('projects.checks_types.dependencies') }
                            {dependencies?.date && (
                                <div className='text-xs'>
                                    { t('projects.checks_done_on') }&nbsp;
                                    { moment.duration(today.diff(dependencies.date)).asHours() < 48 ? (
                                       <ReactTimeAgo locale={i18n.language} date={dependencies.date.toDate()}/>
                                    ) : (
                                        t('projects.checks_done_on_date', { date: dependencies.date.format('ll') })
                                    )}
                                </div>
                            )}
                    </div>
                </div>
                <div className="col-span-full md:col-span-9">
                    { outdatedSections.sections }

                    { isUpToDate && (
                        <div className="flex flex-col items-center">
                            <Icon type="check" size={ 8 }/>
                            <h3 className="italic">
                                { t('projects.dependencies_up_to_date') }
                            </h3>
                        </div>
                    )}

                    { !isUpToDate && !outdatedSections.sections && (
                        <div className="flex flex-col items-center">
                            <Icon type="sad" size={ 8 }/>
                            <h3 className="italic">
                                { t('projects.no_dependencies_checks') }
                            </h3>
                        </div>
                    )}
                </div>
            </div>

        </Card>
    );
}

export default ProjectChecks;
